import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ClipboardList, History, LogOut, PlusCircle, Package } from "lucide-react";

import { InstallPWA } from "@/components/InstallPWA";

interface SellerLayoutProps {
  children: React.ReactNode;
}

export function SellerLayout({ children }: SellerLayoutProps) {
  const { signOut } = useAuth();
  const { data: profile } = useProfile();
  const location = useLocation();

  const firstName = profile?.full_name?.split(" ")[0] || "Vendedor";
  const initials = profile?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "V";

  const navItems = [
    { label: "Painel", icon: ClipboardList, path: "/vendedor" },
    { label: "Novo Lead", icon: PlusCircle, path: "/vendedor/novo-lead" },
    { label: "Sistemas", icon: Package, path: "/vendedor/sistemas" },
    { label: "Histórico", icon: History, path: "/vendedor/historico" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-md bg-white/20 flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">DI</span>
          </div>
          <div>
            <p className="font-semibold text-sm text-primary-foreground">Digital Ideia</p>
            <p className="text-xs text-primary-foreground/70">
              Olá, {firstName} 👋
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8 border-2 border-primary-foreground/30">
            <AvatarFallback className="bg-white/20 text-primary-foreground text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <Button variant="ghost" size="icon" onClick={() => signOut()} className="text-primary-foreground hover:bg-white/10">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 pb-20 bg-muted/30">{children}</main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-md border-t border-border flex justify-around py-2 px-1 z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                isActive
                  ? "text-primary bg-primary/10 font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <item.icon className={`h-5 w-5 ${isActive ? "stroke-[2.5]" : ""}`} />
              <span className="text-[10px]">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <InstallPWA />
    </div>
  );
}
